/**
 * Boundary between the UI's lowercase/snake-case literal unions used in
 * `DataFormatValues` and the backend's UPPERCASE GraphQL enums.
 *
 * Hoisted out of `QuerySettingsPanel/settingsMapper.ts` so both
 * `QuerySettingsPanel` (query-level format) and `FieldSettingsPanel`
 * (field-level format override) can import from a single source —
 * neither panel reaches into the other's mapper.
 *
 * Exhaustive `Record`s — TS won't compile until both sides match. Adding
 * a UI option forces an update here before the form can save.
 */

import type {
  CurrencySymbolPositionOption,
  NegativeNumbersOption,
  NullDisplayOption,
  NumberScaleOption,
  ZeroDisplayOption,
} from "./types";
import type {
  CurrencySymbolPosition,
  NegativeNumberStyle,
  NullDisplayStyle,
  NumberScale,
  ZeroDisplayStyle,
} from "~/graphql/supergraphTypes";

export const NEGATIVE_NUMBERS_TO_BE: Record<NegativeNumbersOption, NegativeNumberStyle> = {
  parentheses: "PARENS",
  minus: "MINUS",
  absolute: "ABSOLUTE",
};

export const NEGATIVE_NUMBERS_FROM_BE: Record<NegativeNumberStyle, NegativeNumbersOption> = {
  PARENS: "parentheses",
  MINUS: "minus",
  ABSOLUTE: "absolute",
};

export const NUMBER_SCALE_TO_BE: Record<NumberScaleOption, NumberScale> = {
  as_is: "AS_IS",
  thousands: "THOUSANDS",
};

export const NUMBER_SCALE_FROM_BE: Record<NumberScale, NumberScaleOption> = {
  AS_IS: "as_is",
  THOUSANDS: "thousands",
};

export const CURRENCY_POSITION_TO_BE: Record<
  CurrencySymbolPositionOption,
  CurrencySymbolPosition
> = {
  left: "LEFT",
  left_space: "LEFT_WITH_SPACE",
  right: "RIGHT",
  right_space: "RIGHT_WITH_SPACE",
};

export const CURRENCY_POSITION_FROM_BE: Record<
  CurrencySymbolPosition,
  CurrencySymbolPositionOption
> = {
  LEFT: "left",
  LEFT_WITH_SPACE: "left_space",
  RIGHT: "right",
  RIGHT_WITH_SPACE: "right_space",
};

export const ZERO_DISPLAY_TO_BE: Record<ZeroDisplayOption, ZeroDisplayStyle> = {
  number: "NUMBER",
  em_dash: "DASH",
  en_dash: "EN_DASH",
  blank: "BLANK",
  na: "NA",
};

export const ZERO_DISPLAY_FROM_BE: Record<ZeroDisplayStyle, ZeroDisplayOption> = {
  NUMBER: "number",
  DASH: "em_dash",
  EN_DASH: "en_dash",
  BLANK: "blank",
  NA: "na",
};

export const NULL_DISPLAY_TO_BE: Record<NullDisplayOption, NullDisplayStyle> = {
  number: "NUMBER",
  blank: "BLANK",
  em_dash: "DASH",
  en_dash: "EN_DASH",
  na: "NA",
};

export const NULL_DISPLAY_FROM_BE: Record<NullDisplayStyle, NullDisplayOption> = {
  NUMBER: "number",
  BLANK: "blank",
  DASH: "em_dash",
  EN_DASH: "en_dash",
  NA: "na",
};
